import { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import type { ILookerDashboard } from './types';

interface LookerDashboardsNavProps {
  dashboardsByFolder: Map<string, ILookerDashboard[]>;
  selectedDashboardId: string | null;
  onSelect: (dashboardId: string) => void;
}

/**
 * Left column of the Looker dashboards page. Shows a scrollable, folder-grouped
 * list of dashboards, mirroring the CatalogItemEdit variant list's layout.
 */
const LookerDashboardsNav = ({ dashboardsByFolder, selectedDashboardId, onSelect }: LookerDashboardsNavProps) => {
  const folders = Array.from(dashboardsByFolder.keys());
  const [openFolders, setOpenFolders] = useState<Set<string>>(() => new Set());

  const toggleFolder = (folder: string) => {
    setOpenFolders(prev => {
      const next = new Set(prev);
      if (next.has(folder)) {
        next.delete(folder);
      } else {
        next.add(folder);
      }
      return next;
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={600} noWrap>
          Dashboards
        </Typography>
      </Box>
      <List sx={{ overflow: 'auto', flex: 1, py: 0 }}>
        {folders.map(folder => {
          const dashboards = dashboardsByFolder.get(folder) ?? [];
          const isOpen = openFolders.has(folder);

          return (
            <Box key={folder}>
              <ListItemButton onClick={() => toggleFolder(folder)} sx={{ py: 1 }}>
                <ListItemText
                  primary={folder}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                />
                <Chip label={dashboards.length} size="small" sx={{ mr: 1, flexShrink: 0 }} />
                {isOpen ? <ExpandLess sx={{ flexShrink: 0 }} /> : <ExpandMore sx={{ flexShrink: 0 }} />}
              </ListItemButton>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {dashboards.map(dashboard => (
                    <ListItemButton
                      key={dashboard.id}
                      selected={selectedDashboardId === dashboard.id}
                      onClick={() => onSelect(dashboard.id)}
                      sx={{ py: 0.75, pl: 4 }}
                    >
                      <ListItemText
                        primary={dashboard.title}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: selectedDashboardId === dashboard.id ? 700 : 400,
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>
    </Box>
  );
};

export default LookerDashboardsNav;
